import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '../../lib/utils'
import PhoneInputWithCountry from '../ui/phone-number-input'

/**
 * Outlined floating-label input used across the auth flow.
 * Label starts as placeholder text inside, then moves to the top border.
 * Input height stays fixed.
 */
export const AuthFloatingInput = ({
  id,
  label,
  value = '',
  onChange,
  onBlur,
  type = 'text',
  required = false,
  className,
  inputClassName,
  name,
  darkSurface = false,
  ...props
}) => {
  const generatedId = useId()
  const inputId = id || generatedId
  const [focused, setFocused] = useState(false)
  const hasValue = value !== undefined && value !== null && String(value).length > 0
  const floated = focused || hasValue

  return (
    <div className={cn('relative h-14 w-full', className)}>
      <input
        id={inputId}
        name={name || id}
        type={type}
        value={value}
        required={required}
        placeholder=' '
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false)
          onBlur?.(e)
        }}
        className={cn(
          'h-full w-full rounded-xl border-2 bg-transparent px-3 font-IBM text-base outline-none transition-colors',
          darkSurface
            ? 'text-white lg:text-black-950 dark:lg:text-[var(--fb-text)]'
            : 'text-black-950 dark:text-[var(--fb-text)]',
          floated
            ? 'border-primary-500'
            : darkSurface
            ? 'border-[#3a3d56] lg:border-[#D3DBEC] dark:lg:border-[var(--fb-border-strong)]'
            : 'border-[#D3DBEC] dark:border-[var(--fb-border-strong)]',
          inputClassName,
        )}
        {...props}
      />
      <label
        htmlFor={inputId}
        className={cn(
          'pointer-events-none absolute left-3 z-10 font-IBM transition-all duration-200 ease-out',
          floated
            ? cn(
                'top-0 -translate-y-1/2 px-1 text-xs font-medium text-primary-500',
                // Match page/surface so the border is masked behind the label
                darkSurface
                  ? 'bg-[#040313] lg:bg-white dark:lg:bg-[var(--fb-surface)]'
                  : 'bg-white dark:bg-[var(--fb-surface)]',
              )
            : 'top-1/2 -translate-y-1/2 text-base font-medium text-black-400',
        )}
      >
        {label}
      </label>
    </div>
  )
}

/**
 * Floating-label phone field.
 * Empty/unfocused: label acts as placeholder.
 * Focused/filled: label sits on the top border; country selector, code, and placeholder show.
 */
export const AuthFloatingPhoneInput = ({
  label = 'Enter Phone Number',
  phoneNumber,
  setPhoneNumber,
  className,
  onFocus,
  onBlur,
  darkSurface = false,
}) => {
  const wrapRef = useRef(null)
  const [focused, setFocused] = useState(false)
  const digitCount = String(phoneNumber || '').replace(/\D/g, '').length
  const hasValue = digitCount > 2
  const floated = focused || hasValue

  useEffect(() => {
    if (!focused) return
    const input = wrapRef.current?.querySelector('input')
    if (input && document.activeElement !== input) {
      input.focus()
    }
  }, [focused])

  return (
    <div
      ref={wrapRef}
      role='button'
      tabIndex={floated ? -1 : 0}
      className={cn(
        'auth-floating-phone relative flex h-14 w-full items-center rounded-xl border-2 px-3 transition-colors',
        darkSurface && 'auth-floating-phone--dark',
        floated
          ? 'border-primary-500'
          : darkSurface
          ? 'cursor-text border-[#3a3d56] lg:border-[#D3DBEC] dark:lg:border-[var(--fb-border-strong)]'
          : 'cursor-text border-[#D3DBEC] dark:border-[var(--fb-border-strong)]',
        className,
      )}
      onClick={() => {
        if (!floated) setFocused(true)
      }}
      onKeyDown={(e) => {
        if (!floated && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          setFocused(true)
        }
      }}
      onFocusCapture={() => {
        setFocused(true)
        onFocus?.()
      }}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setFocused(false)
          onBlur?.(e)
        }
      }}
    >
      <style>{`
        .auth-floating-phone input,
        .auth-floating-phone button,
        html.dark .auth-floating-phone input,
        html.dark .auth-floating-phone button {
          background: transparent !important;
          background-color: transparent !important;
          border-color: transparent !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
        }
        .auth-floating-phone--dark input,
        .auth-floating-phone--dark button {
          color: #fff !important;
        }
        @media (min-width: 1024px) {
          .auth-floating-phone--dark input,
          .auth-floating-phone--dark button {
            color: inherit !important;
          }
        }
        .auth-floating-phone .not-prose {
          margin-top: 0 !important;
          width: 100%;
        }
        .auth-floating-phone input::placeholder {
          color: transparent !important;
          opacity: 0 !important;
        }
        .auth-floating-phone input:-webkit-autofill,
        .auth-floating-phone input:-webkit-autofill:hover,
        .auth-floating-phone input:-webkit-autofill:focus,
        html.dark .auth-floating-phone input:-webkit-autofill {
          -webkit-text-fill-color: inherit !important;
          transition: background-color 9999s ease-in-out 0s !important;
          box-shadow: 0 0 0 1000px transparent inset !important;
          -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
        }
        .auth-floating-phone--dark input:-webkit-autofill,
        .auth-floating-phone--dark input:-webkit-autofill:hover,
        .auth-floating-phone--dark input:-webkit-autofill:focus {
          -webkit-text-fill-color: #fff !important;
        }
        @media (min-width: 1024px) {
          .auth-floating-phone--dark input:-webkit-autofill,
          .auth-floating-phone--dark input:-webkit-autofill:hover,
          .auth-floating-phone--dark input:-webkit-autofill:focus {
            -webkit-text-fill-color: inherit !important;
          }
        }
      `}</style>

      <label
        className={cn(
          'pointer-events-none absolute z-20 font-IBM transition-all duration-200 ease-out',
          floated
            ? cn(
                'left-3 top-0 -translate-y-1/2 px-1 text-xs font-medium text-primary-500',
                // Match page/surface so the border is masked behind the label
                darkSurface
                  ? 'bg-[#040313] lg:bg-white dark:lg:bg-[var(--fb-surface)]'
                  : 'bg-white dark:bg-[var(--fb-surface)]',
              )
            : 'left-3 top-1/2 -translate-y-1/2 text-base font-medium text-black-400',
        )}
      >
        {label}
      </label>

      <div
        className={cn(
          'w-full [&_button]:!h-9 [&_button]:!border-0 [&_button]:!px-0 [&_button]:!shadow-none [&_input]:!h-9 [&_input]:!rounded-none [&_input]:!border-0 [&_input]:!px-1 [&_input]:!text-base [&_input]:!shadow-none [&_input]:focus-visible:!ring-0 [&_input]:focus-visible:!ring-offset-0',
          !floated && 'pointer-events-none invisible',
        )}
        aria-hidden={!floated}
      >
        <PhoneInputWithCountry phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber} />
      </div>
    </div>
  )
}
